import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api from "@/lib/api";
import { startKeepWarm, stopKeepWarm } from "@/lib/keepWarm";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch (error) {
        if (error?.response?.status !== 401) {
          // 401 just means "not signed in yet" — anything else is worth logging
          console.warn("Auth check failed:", error?.message || error);
        }
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Client-side keep-warm: while any authenticated user is browsing the app,
  // ping /api/health every 4 min so the deployed pod never gets a chance to cool down.
  useEffect(() => {
    if (user && user !== false) {
      startKeepWarm();
      return () => stopKeepWarm();
    }
    return undefined;
  }, [user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Token is also set as an httpOnly cookie by the backend; localStorage is a
    // Bearer fallback for cross-origin preview where SameSite=None cookies may drop.
    if (data.token) localStorage.setItem("rwm_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.warn("Logout request failed:", error?.message || error);
    }
    localStorage.removeItem("rwm_token");
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
